import type { QuestionnaireQuestion } from '../types/questionnaire';

const isFilled = (value: unknown): boolean => (
  typeof value === 'string' && value.trim().length > 0
);

/** True when a follow-up field is marked required (adult templates have none). */
export const hasRequiredFollowUp = (question: QuestionnaireQuestion): boolean => (
  !!question.conditionalFollowUp?.fields.some((field) => field.required)
);

const areRequiredFollowUpsFilled = (
  question: QuestionnaireQuestion,
  answer: Record<string, unknown>,
): boolean => {
  const followUp = question.conditionalFollowUp;
  if (!followUp || !hasRequiredFollowUp(question)) return true;
  if (!followUp.triggerValues.includes(answer.answer as string)) return true;
  return followUp.fields
    .filter((field) => field.required)
    .every((field) => isFilled(answer[field.id]));
};

export const isQuestionAnswered = (
  question: QuestionnaireQuestion,
  answers: Record<string, any>,
): boolean => {
  const answer = answers[question.id];
  if (answer === undefined || answer === null || answer === '') return false;
  switch (question.type) {
    case 'dual-period':
      if (question.examplesOptional) {
        return answer.answer === 'No' || answer.answer === 'Yes';
      }
      return answer.answer === 'No'
        || !!(answer.answer === 'Yes'
          && (answer.adult?.length > 0 || answer.adultOther?.trim())
          && (answer.child?.length > 0 || answer.childOther?.trim()));
    case 'radio':
      if (question.conditionalFollowUp && typeof answer === 'object') {
        return !!answer?.answer && areRequiredFollowUpsFilled(question, answer);
      }
      return !!answer;
    case 'frequency':
      return !!answer;
    case 'checkbox':
      return Array.isArray(answer) && answer.length > 0;
    case 'text':
      return typeof answer === 'string' && answer.trim().length > 0;
    case 'frequency-with-followup':
      return !!answer?.freq;
    default:
      return !!answer;
  }
};
